import React from 'react';
import { Heart } from 'lucide-react';
import type { FooterProps } from '../../types/Footer.types';

/**
 * Footer organism component
 * 
 * Simple footer with "Build with ♥ by Ideoz" message.
 * Used at the bottom of landing pages and sidebar layouts.
 * 
 * Features:
 * - Two size variants (sm, md)
 * - Optional top border
 * - I18n text support
 * - Heart icon from lucide-react
 * 
 * @example
 * ```tsx
 * // Default (medium size, no border)
 * <Footer />
 * 
 * // Small with border (for sidebars)
 * <Footer size="sm" hasBorder />
 * 
 * // With i18n
 * <Footer 
 *   buildWithText={t('footer.buildWith')}
 *   byText={t('footer.byIdeoz')}
 * />
 * 
 * // Full customization
 * <Footer
 *   size="sm"
 *   hasBorder
 *   buildWithText="Made with"
 *   byText="by Our Team"
 *   className="bg-gray-50"
 * />
 * ```
 */
export const Footer: React.FC<FooterProps> = ({
  size = 'md',
  hasBorder = false,
  buildWithText = 'Build with',
  byText = 'by ideoz from Middle East to the World',
  className = '',
  testId = 'footer'
}) => {
  // Size classes mapping
  const sizeClasses = {
    sm: 'text-[9px]',
    md: 'text-sm'
  };
  
  const textSizeClass = sizeClasses[size];
  
  return (
    <footer 
      className={`py-6 mt-auto ${hasBorder ? 'border-t border-gray-200' : ''} ${className}`.trim()}
      data-testid={testId}
    >
      <div className="flex justify-center w-full">
        <p className={`text-gray-600 text-center font-medium ${textSizeClass}  flex items-center gap-1`}>
          <span>{buildWithText}</span>
          <Heart 
            size={14} 
            strokeWidth={2} 
            className="text-[#fd5353] fill-[#fd5353]" 
            aria-label="love"
          />
          <span>{byText}</span>
        </p>
      </div>
    </footer>
  );
};

export default Footer;
